import Grid from '@material-ui/core/Grid';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  createStyles,
  IconButton,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { convertToRaw, convertFromRaw, Editor, EditorState } from 'draft-js';

import MoreVertIcon from '@material-ui/icons/MoreVert';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import React, { useEffect, useState } from 'react';

import { baseTheme } from '../../../theme';
import { getLibraryPostNote, updateLibraryNoteAction } from '../../../store/library/actions';
import { useDispatch } from 'react-redux';
import PostEditor from '../../post-components/post-component/post-editor';
import NoSelectWrapper from '../../post-components/post-component/post-editor/no-select-wrapper';
import { getPostHighlights } from '../../../services';
import { IHighLight } from '../../../services/interfaces';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingRight: theme.spacing(0),
      paddingLeft: theme.spacing(0),
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      width: '100%',
    },
    contentWrapper: {
      justifyContent: 'space-between',
    },
    contentTitle: {
      fontWeight: 'bold',
      flexShrink: 0,
    },
    dateDisplay: {
      flexShrink: 0,
      marginTop: 'auto',
      marginBottom: 'auto',
    },
    addedOnDisplay: {
      flexShrink: 0,
      paddingLeft: theme.spacing(1),
      marginTop: 'auto',
      marginBottom: 'auto',
    },
    contentAuthor: {
      color: theme.palette.text.secondary,
    },
    centerText: {
      marginTop: 'auto',
      marginBottom: 'auto',
      fontWeight: 'bold',
    },
    content: {
      border: `solid 1px ${theme.palette.primary.main}`,
      borderRadius: '5px',
      padding: theme.spacing(1),
      maxHeight: '700px',
      overflowX: 'scroll',
    },
    contentBody: {
      width: '100%',
      borderBottom: `solid 1px ${theme.palette.text.disabled}`,
    },
    highlightLabel: {
      width: '100%',
      paddingTop: theme.spacing(4),
    },
    noteHeading: {
      borderBottom: `solid 1px ${theme.palette.primary.main}`,
      paddingBottom: theme.spacing(1),
      marginBottom: theme.spacing(1),
      fontWeight: 'bold',
    },
    notes: {
      padding: `0px ${theme.spacing(1)}px`,
    },
    highLight: {
      width: '100%',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),

    },
    front: {
      flexDirection: 'row',
      display: 'flex',
      width: '70%',
    },
    textColumn: {
      flexDirection: 'column',
      display: 'flex',
    },
    subtitleRow: {
      flexDirection: 'row',
      display: 'flex',
    },
  }),
);

export default function LibraryListItem(props: any) {
  const {
    handleIconClick,
    id,
    title,
    content,
    date,
    addedOn,
    author: {
      displayName: authorName,
    },
    group: {
      name: groupName,
      type: groupType,
    },
    note } = props;
  const classes = useStyles(baseTheme);
  const storedState = EditorState.createWithContent(convertFromRaw(JSON.parse(content)));
  const [storedNote, setNoteState] = useState<string | null>(null);
  const [highlights, setHighlights] = useState<any>([]);
  const dispatch = useDispatch();
  useEffect(() => {
    setHighlightsInState();
  }, []);
  const setHighlightsInState = async () => {
    const highlightsForState =  await getPostHighlights(props.originalId);
    setHighlights(highlightsForState);
  };

  const onExpand = (id: string, dispatch: any) => (event: any) => {
    getLibraryPostNote(id, dispatch);
  };

  const handleNoteChanges = (state: any) => {
    const content = JSON.stringify(convertToRaw(state.getCurrentContent()));
    setNoteState(content);
  };

  const saveNoteChanges = () => {
    updateLibraryNoteAction({ id, note: storedNote });
  };

  const renderHighlights = (highlights: IHighLight[]) =>
      highlights ?
          highlights.map((highlight: IHighLight) => (
              <Grid item key={highlight.id} className={classes.highLight}>
                {highlight.content}
              </Grid>
          ))
          : null;
  return (
    <Accordion
      classes={{
        root: classes.root,
      }}
      TransitionProps={{ unmountOnExit: true }}
      onChange={onExpand(id, dispatch)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="library-post-title"
        id="library-post-title"
        classes={{ content: classes.contentWrapper }}
      >
        <div className={classes.front}>
          <IconButton
            color={groupType === 'Public' ? 'primary' : 'secondary'}
            onClick={handleIconClick(props.id)}
          >
            <MoreVertIcon />
          </IconButton>
          <div className={classes.textColumn}>
            <Typography className={`${classes.contentTitle} ${classes.centerText}`}>
              {title}
            </Typography>
            <div className={classes.subtitleRow}>
              <Typography className={`${classes.dateDisplay}`} variant={'subtitle1'}>
                Posted: {date}
              </Typography>
              <Typography className={`${classes.addedOnDisplay}`} variant={'subtitle1'}>
                Added: {addedOn}
              </Typography>
            </div>
          </div>
        </div>

        <Typography className={`${classes.contentAuthor}  ${classes.centerText}`}>
          Group: {groupName}, Author: {authorName}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container>
          <Grid container item xs={6} className={classes.content}>
            <div className={classes.contentBody}>
              <NoSelectWrapper>
                <Editor
                  editorState={storedState}
                  readOnly={true}
                  onChange={() => { }}
                />
              </NoSelectWrapper>
            </div>
            {highlights.length > 0 &&
            <div className={classes.highlightLabel}>
              Highlights:
            </div>}
            {renderHighlights(highlights)}
          </Grid>
          <Grid container item xs={6} className={classes.notes} direction="column">
            <Grid item className={classes.noteHeading}>
              Add Note, Affirmation, Reminder, or Addendum.
              This note is only accessible by you.<br />
              Your changes will be saved automatically.
            </Grid>
            <PostEditor
              data={note?.note}
              label={'Note'}
              pixelHeight={300}
              handleChanges={handleNoteChanges}
              onBlur={saveNoteChanges}
              customControls={[
                // {
                //   name: 'myThing',
                //   icon: <InvertColorsIcon />,
                //   type: 'inline',
                // },
                // {
                //   name: "clear-callback",
                //   icon: <InvertColorsIcon />,
                //   type: "callback",
                //   onClick: () => {
                //     alert('I did it!')
                //   }
                // }
              ]}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
